import * as ActionCable from 'actioncable'
import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory'
import { ApolloClient } from 'apollo-client'
import { GraphQLError } from 'graphql'
import { setContext } from 'apollo-link-context'
import { split } from 'apollo-link'
import { onError } from 'apollo-link-error'
import { HttpLink } from 'apollo-link-http'
import * as React from 'react'
import { ApolloProvider } from 'react-apollo'
import { getToken } from 'web-fns/dist/auth'
import notification from 'web-makeup/dist/Notification'

import ActionCableLink from '../../lib/ActionCableLink'

import introspectionQueryResultData from './fragmentTypes.json'

declare global {
  interface Window {
    env: {
      API_URL: string | undefined
      WS_URL: string
      SENTRY_DSN: string
      GOOGLE_MAPS_API_KEY: string
      GOOGLE_SIGNIN_CLIENT_ID: string
      CLOUDINARY_UPLOAD_PRESET: string
      CLOUDINARY_UPLOAD_URL: string
      CLOUDINARY_DOWNLOAD_URL: string
      CLOUDINARY_USER: string
      CLIENT_APP_ID: string
      FACEBOOK_APP_ID: string
      LINKEDIN_APP_ID: string
      APPLICATION_TAG: string
      SIDE_MENU_BASE_URL: string
      PRODUCER_APP_ID: string
      INTERCOM_APP_ID: string
    }
    intercomSettings: any
  }
}

export const ensureEnvVars = (env, window) => {
  if (env.REACT_APP_USE_MOCK_API === 'true') {
    if (!env.REACT_APP_MOCK_API_URL) {
      throw new ReferenceError(
        'env.REACT_APP_MOCK_API_URL must contain a URI when process.env.REACT_APP_USE_MOCK_API is true.'
      )
    }
    if (window.env) {
      window.env.API_URL = env.REACT_APP_MOCK_API_URL
    }
  }

  // Catch runtime error in case `env.js` is missing `API_URL`.
  if (window.env && !window.env.API_URL) {
    throw new ReferenceError('window.env.API_URL must contain a URI.')
  }
}

ensureEnvVars(process.env, window)

const httpLink = new HttpLink({
  uri: window.env && window.env.API_URL,
  credentials: 'include',
})

// prettier-ignore
const ERRORS = {
  '401-INVALID_APP_ID': 'You are using an invalid app id.',
  '401-INVALID_JWT': 'You are using an invalid JWT.',
  '500': 'Server exploded, oops.',
  'UNAUTHORIZED': 'You are unauthorized to do this action.',
  'defaults': 'Please try again.',
}

type NError = Error & {
  statusCode?: number
  result?: {
    errors?: Array<{ message: string }>
  }
}

export const onErrorHandler = ({
  networkError,
  graphQLErrors,
}: {
  networkError?: Error
  graphQLErrors?: ReadonlyArray<GraphQLError>
}) => {
  if (!networkError && !graphQLErrors) {
    return
  }

  if (graphQLErrors && graphQLErrors.length > 0) {
    for (const error of graphQLErrors) {
      notification.error({
        message: error.message,
        description: ERRORS[error.message] || ERRORS.defaults,
        duration: 0,
      })
    }
  }

  if (networkError) {
    const netError: NError = networkError
    const errorMessage =
      (netError.result && netError.result.errors && netError.result.errors[0].message) || ''
    const errorName = `${netError.statusCode}-${errorMessage}`

    notification.error({
      message: 'A network error has happened.',
      description: ERRORS[errorName] || ERRORS.defaults,
      duration: 0,
    })
  }
}

// Try to handle network errors here since network errors
// are likely to be generic.
const errorLink = onError(onErrorHandler)

export const contextHandler = (_, { headers }) => {
  // get the authentication token from local storage if it exists
  const jwt = getToken()
  return {
    headers: {
      ...headers,
      Authorization: jwt ? `Bearer ${jwt}` : '',
      'X-APP-ID': window.env && window.env.CLIENT_APP_ID,
    },
  }
}

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
})

const cache = new InMemoryCache({ fragmentMatcher })

const cable = ActionCable.createConsumer(window.env && window.env.WS_URL)

const hasSubscriptionOperation = ({ query: { definitions } }) =>
  definitions.some(
    ({ kind, operation }) => kind === 'OperationDefinition' && operation === 'subscription'
  )

const link = split(hasSubscriptionOperation, ActionCableLink({ cable }), errorLink.concat(httpLink))

const headersLink = setContext(contextHandler)

const client = new ApolloClient({
  link: headersLink.concat(link),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'ignore',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'none',
    },
    mutate: {
      errorPolicy: 'none',
    },
  },
})

interface Props {
  children: React.ReactElement<{}>
}

const GraphQLProvider: React.SFC<Props> = ({ children }: Props) => (
  <ApolloProvider client={client}>{children}</ApolloProvider>
)

export default GraphQLProvider



// WEBPACK FOOTER //
// ./src/components/ApolloProvider/ApolloProvider.tsx